/**
 * LiveRegion Component
 *
 * Accessible live region for announcing dynamic content changes to screen readers.
 * Supports both polite and assertive announcements.
 *
 * Usage:
 *   import { LiveRegion, useLiveAnnounce } from './ui/LiveRegion';
 *
 *   // In component:
 *   const announce = useLiveAnnounce();
 *   announce('Loading complete', 'polite');
 *
 * @module components/ui/LiveRegion
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';

// Context for live announcements
const LiveRegionContext = createContext(null);

/**
 * Screen reader only styles
 */
const srOnlyStyles = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/**
 * LiveRegion - Invisible ARIA live region for screen reader announcements
 *
 * Place this once near the root of your app. Use useLiveAnnounce() hook
 * to make announcements from any component.
 */
export const LiveRegion = ({ children }) => {
  const [politeMessage, setPoliteMessage] = useState('');
  const [assertiveMessage, setAssertiveMessage] = useState('');
  const [politeKey, setPoliteKey] = useState(0);
  const [assertiveKey, setAssertiveKey] = useState(0);

  /**
   * Announce a message to screen readers
   * @param {string} message - Message to announce
   * @param {('polite'|'assertive')} politeness - How urgently to announce
   */
  const announce = useCallback((message, politeness = 'polite') => {
    if (politeness === 'assertive') {
      setAssertiveMessage(message);
      setAssertiveKey(k => k + 1);
    } else {
      setPoliteMessage(message);
      setPoliteKey(k => k + 1);
    }
  }, []);

  /**
   * Clear all announcements
   */
  const clear = useCallback(() => {
    setPoliteMessage('');
    setAssertiveMessage('');
  }, []);

  // Clear messages after they've been read (after a delay)
  useEffect(() => {
    if (politeMessage) {
      const timer = setTimeout(() => setPoliteMessage(''), 1000);
      return () => clearTimeout(timer);
    }
  }, [politeMessage, politeKey]);

  useEffect(() => {
    if (assertiveMessage) {
      const timer = setTimeout(() => setAssertiveMessage(''), 1000);
      return () => clearTimeout(timer);
    }
  }, [assertiveMessage, assertiveKey]);

  return (
    <LiveRegionContext.Provider value={{ announce, clear }}>
      {children}

      {/* Polite live region - waits for user to pause */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={srOnlyStyles}
      >
        {politeMessage}
      </div>

      {/* Assertive live region - interrupts immediately */}
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        style={srOnlyStyles}
      >
        {assertiveMessage}
      </div>
    </LiveRegionContext.Provider>
  );
};

LiveRegion.propTypes = {
  children: PropTypes.node,
};

/**
 * Hook to announce messages to screen readers
 * @returns {(message: string, politeness?: 'polite' | 'assertive') => void}
 */
export const useLiveAnnounce = () => {
  const context = useContext(LiveRegionContext);

  if (!context) {
    // Return a no-op if used outside provider (for resilience)
    return () => {
      console.warn('useLiveAnnounce used outside LiveRegion provider');
    };
  }

  return context.announce;
};

/**
 * Hook to get full live region context
 */
export const useLiveRegion = () => {
  const context = useContext(LiveRegionContext);
  return context || { announce: () => {}, clear: () => {} };
};

/**
 * LoadingAnnouncer - Automatic loading state announcements
 *
 * Place this component where you have loading states. It will
 * announce loading start and completion to screen readers.
 */
export const LoadingAnnouncer = ({
  isLoading,
  loadingMessage = 'Loading...',
  loadedMessage = 'Content loaded',
  errorMessage = 'Error loading content',
  error = null,
}) => {
  const announce = useLiveAnnounce();
  const [wasLoading, setWasLoading] = useState(false);

  useEffect(() => {
    if (isLoading && !wasLoading) {
      // Just started loading
      announce(loadingMessage, 'polite');
      setWasLoading(true);
    } else if (!isLoading && wasLoading) {
      // Just finished loading
      if (error) {
        announce(errorMessage || error.message, 'assertive');
      } else {
        announce(loadedMessage, 'polite');
      }
      setWasLoading(false);
    }
  }, [isLoading, wasLoading, error, announce, loadingMessage, loadedMessage, errorMessage]);

  return null; // This component renders nothing visible
};

LoadingAnnouncer.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  loadingMessage: PropTypes.string,
  loadedMessage: PropTypes.string,
  errorMessage: PropTypes.string,
  error: PropTypes.object,
};

/**
 * ProgressAnnouncer - Announces progress updates at intervals
 *
 * Useful for long-running operations where you want to keep
 * users informed of progress.
 */
export const ProgressAnnouncer = ({
  progress,
  total = 100,
  intervalPercent = 25,
  prefix = 'Progress',
}) => {
  const announce = useLiveAnnounce();
  const [lastAnnounced, setLastAnnounced] = useState(0);

  useEffect(() => {
    const percentComplete = Math.round((progress / total) * 100);
    const interval = Math.floor(percentComplete / intervalPercent) * intervalPercent;

    if (interval > lastAnnounced && interval <= 100) {
      announce(`${prefix}: ${interval}% complete`, 'polite');
      setLastAnnounced(interval);
    }

    // Announce completion
    if (percentComplete >= 100 && lastAnnounced < 100) {
      announce(`${prefix}: Complete`, 'polite');
      setLastAnnounced(100);
    }
  }, [progress, total, intervalPercent, prefix, announce, lastAnnounced]);

  return null;
};

ProgressAnnouncer.propTypes = {
  progress: PropTypes.number.isRequired,
  total: PropTypes.number,
  intervalPercent: PropTypes.number,
  prefix: PropTypes.string,
};

export default LiveRegion;
